import { useEffect, useId, useState } from 'react';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { SelectPopover } from '../../../components/SelectPopover';
import { TextInput } from '../../../components/TextInput';
import type { BillingInvoice, BillingPaymentMethod, MarkInvoicePaidRequest } from '../../../types/adminBilling';
import {
  ADMIN_PAID_METHODS,
  PAID_METHOD_LABEL,
  centsToDollarsInput,
  dollarsToCents,
  formatMoney,
} from './billingFormat';

/*
  Only the three an admin may choose. `Stripe` is a fourth stored value, written by the mirror to
  mean "Stripe collected it, by whatever means", and the API refuses it from a person: somebody
  recording a cheque has seen the cheque.
*/
const METHOD_LABELS = ADMIN_PAID_METHODS.map((method) => PAID_METHOD_LABEL[method]);

const methodFromLabel = (label: string): BillingPaymentMethod =>
  ADMIN_PAID_METHODS.find((method) => PAID_METHOD_LABEL[method] === label) ?? 'Manual';

interface MarkInvoicePaidModalProps {
  invoice: BillingInvoice | null;
  saving: boolean;
  error: string | null;
  onConfirm: (request: MarkInvoicePaidRequest) => void;
  onClose: () => void;
}

/**
 * Record a payment against one invoice.
 *
 * The amount defaults to what is still owed, because a part payment is the exception. It is still
 * editable, since the alternative is an admin having no way to record the $900 that arrived
 * against a $1000 invoice.
 *
 * **The paid date is sent as UTC midnight of the day that was picked.** A date input has no time
 * and no zone; turning it into "now in the admin's browser" would record a payment at a moment
 * nobody chose, and the day itself is what an accounts team reconciles against.
 */
export const MarkInvoicePaidModal = ({
  invoice,
  saving,
  error,
  onConfirm,
  onClose,
}: MarkInvoicePaidModalProps) => {
  const [method, setMethod] = useState<BillingPaymentMethod>('Manual');
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const methodId = useId();

  useEffect(() => {
    if (!invoice) return;
    setMethod('Manual');
    /*
      What is still owed, not the face value. This field is the amount received in THIS payment, so
      on an invoice that has already been part paid the face value prefills a second full payment —
      and the API treats an overpayment as valid, so nothing would have refused it.
    */
    setAmount(centsToDollarsInput(invoice.amountRemainingCents ?? invoice.amountCents));
    setPaidOn('');
    setAmountError(null);
  }, [invoice]);

  const handleConfirm = () => {
    const paidAmountCents = dollarsToCents(amount);
    if (paidAmountCents === null || paidAmountCents <= 0) {
      setAmountError('Enter the amount that was paid, in dollars.');
      return;
    }
    setAmountError(null);

    onConfirm({
      method,
      paidAmountCents,
      ...(/^\d{4}-\d{2}-\d{2}$/.test(paidOn) ? { paidAt: `${paidOn}T00:00:00.000Z` } : {}),
    });
  };

  return (
    <ConfirmModal
      isOpen={invoice !== null}
      title="Mark invoice paid"
      message={
        invoice
          ? `Record a payment against the ${formatMoney(
              invoice.amountCents,
              invoice.currency
            )} invoice.`
          : ''
      }
      confirmText={saving ? 'Saving...' : 'Mark paid'}
      busy={saving}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="billing-form">
        <div>
          <label className="billing-panel__label" htmlFor={methodId}>
            Paid by
          </label>
          <SelectPopover
            id={methodId}
            options={METHOD_LABELS}
            value={PAID_METHOD_LABEL[method]}
            onChange={(label) => setMethod(methodFromLabel(label))}
            placeholder="How it was paid"
            clearable={false}
            disabled={saving}
            fullWidth
            testId="billing-paid-method"
          />
        </div>
        <TextInput
          label="Amount paid (dollars)"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amountError ?? undefined}
          disabled={saving}
          data-testid="billing-paid-amount"
        />
        <TextInput
          label="Paid on (optional)"
          type="date"
          value={paidOn}
          onChange={(e) => setPaidOn(e.target.value)}
          disabled={saving}
          data-testid="billing-paid-on"
        />
        <p className="billing-form__hint">
          Leave the date blank to record the payment as now. A date is recorded as UTC, so every
          admin sees the day that was picked.
        </p>
        {error && (
          <p className="billing-form__error" role="alert" data-testid="billing-paid-error">
            {error}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
};
